/**
 * The Transport is the abstraction over "where the simulation runs".
 *
 * In the singleplayer MVP it's just an in-process function call: the client
 * reads its own inputs, the client steps the simulation, and the client draws
 * the result. This module wraps that up behind an interface so the game loop
 * never reaches into global state.
 *
 * When multiplayer lands, `LocalTransport` will be replaced with a
 * `WebSocketTransport` that sends inputs to a Durable Object and receives
 * authoritative state diffs back. Nothing in `game/loop.ts` or any of the
 * rendering code will need to change.
 */

export interface InputFrame {
  tick: number;
  move: { x: number; z: number };
  yaw: number;
  pitch: number;
  flap: boolean;
  peck: boolean;
  peckHeld: boolean;
  sprint: boolean;
}

export interface StateFrame {
  tick: number;
}

export interface Transport {
  sendInput(input: InputFrame): void;
  onState(listener: (state: StateFrame) => void): () => void;
  close(): void;
}

/**
 * The singleplayer "transport" — in-process, synchronous, zero latency.
 *
 * The simulation is the authority here; we just forward state frames it emits
 * so the same listener-based contract works for both local and networked play.
 */
export class LocalTransport implements Transport {
  private listeners = new Set<(state: StateFrame) => void>();

  sendInput(_input: InputFrame): void {
    /* no-op: the simulation reads inputs directly via the game loop in
       singleplayer. This exists so code paths match the multiplayer case. */
  }

  emitState(state: StateFrame): void {
    for (const listener of this.listeners) listener(state);
  }

  onState(listener: (state: StateFrame) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  close(): void {
    this.listeners.clear();
  }
}
